/**
 * Operations UI (M3 S4) — the browser wrapper over operations-core.js.
 *
 * Layout is the INVERSE of the raid view on purpose: the whole world map at the
 * top, controls beneath it. A raid path is built by clicking rooms in sequence;
 * a route is chosen by looking at where things are relative to each other, so
 * the map has to be the thing you read first.
 *
 * The map is a plain CSS grid, NOT the raid grid renderer. The spec suggested
 * reusing it, but that renderer paints a procedural canvas per tile and is
 * wired to raid state throughout; a world map needs five region pins and some
 * terrain shading. Reuse would have cost more than it saved.
 *
 * All rules live in operations-core.js — this file renders and reports.
 */

import * as config from '../constants.js';
import { gameState, saveGameState } from './gameState.js';
import { addNotification } from './notifications.js';
import { updateUI } from './game.js';
import {
    OPERATION_TEMPLATES, getWorldGrid, tileCost, regionTile, operationRegions,
    findRoute, routeProgress, originOffers, routeFromWaypoints, availableLeads, operationSlots, operationsInFlight,
    planCargo, canLaunchOperation, launchOperationCore, operationYield, settleOperationsCore,
    energyFor, operationStores, signatureStock,
} from './operations-core.js';
import { getCharterRank } from './syndicate-core.js';
import {
    demandMultiplier, factionDemandMultiplier, baseFactionDemand, earnsDemandWrit,
    satiationLevel, satiationRecoveryMs, DEMAND_MIN,
} from './demand-core.js';
import { getItemName, getSkillFaction } from './util.js';
import { getRegionPhase, getTilePhase, daylightFraction } from './worldClock-core.js';

const GOLD = '#e0a84c';
const GOLD_DIM = '#8a6526';

/**
 * Territory colour per region.
 *
 * Placeholder until tiles are procedurally generated (no spec yet): the job for
 * now is only that a player can see WHICH region a tile belongs to. Hues are
 * spread far enough apart to stay distinct after terrain shading darkens them.
 */
const REGION_COLOR = {
    'cradle-expanse': '#7a5a3c',        // ochre
    'shard-cities': '#4a5a7a',          // slate blue
    'verdant-reclamation': '#3f6b45',   // green
    'sunken-bastions': '#2f6b6b',       // teal
    'hollow-core': '#6b3f5f',           // plum
};

/**
 * Terrain modulates the territory colour rather than replacing it, so a tile
 * answers both questions at once: whose is it, and what is the ground like.
 * `mix` is how far toward the terrain tint the region colour is dragged.
 */
const TERRAIN_STYLE = {
    '.': { tint: null, mix: 0, label: 'Open ground', cost: 1 },
    // Highland LIGHTENS. Darkening made high ground read as shadow, which is
    // backwards — at this scale a highland is a snowline, and the eye reads
    // pale as "up".
    '^': { tint: '#dfe7f0', mix: 0.34, label: 'Highland', cost: 2 },
    // Ocean is very nearly uniform, and dark. It used to blend only halfway to
    // a mid blue, which left every sea tile carrying its region's hue — the map
    // read as five vertical colour bands with invisible coastlines. Darkening
    // it further is what separates the sea from the two regions whose own
    // colours are blue-ish (Shard Cities slate, Flooded Bastions teal); at a
    // lighter value their coastlines disappeared into it.
    '~': { tint: '#061424', mix: 0.92, label: 'Ocean', cost: 3 },
    '#': { tint: '#000000', mix: 0.85, label: 'Impassable', cost: null },
};

/** Blend two hex colours. Keeps territory legible under terrain shading. */
function mixHex(base, tint, amount) {
    if (!tint || amount <= 0) return base;
    const parse = (h) => [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16));
    const [r1, g1, b1] = parse(base);
    const [r2, g2, b2] = parse(tint);
    const blend = (a, b) => Math.round(a + (b - a) * amount).toString(16).padStart(2, '0');
    return `#${blend(r1, r2)}${blend(g1, g2)}${blend(b1, b2)}`;
}

/** Distinct colours for each convoy in transit, so routes never merge visually. */
const CONVOY_COLORS = ['#6ee7ff', '#ff9de2', '#ffe066'];

/** Green marks a delivery that also earns the Writ trickle. */
const DEMAND_HOT = '#7df5a0';

/**
 * The demand premium, rendered.
 *
 * Two states worth distinguishing, not one: any premium is worth seeing, but the
 * Writ threshold is a cliff — cross it and a Long Haul pays 2📜 instead of 1 —
 * and a cliff nobody can see is a cliff nobody plans around.
 */
/**
 * Would a delivery at this multiplier actually bank a Writ *as drafted*?
 *
 * BOTH halves have to hold: the market must be hungry AND the class must be
 * Writ-eligible (only Grand Circuit is — see OPERATION_TEMPLATES). Reading the
 * demand alone stamped 📜 on every hungry destination on the map even while a
 * Short Haul was selected, promising a reward that class can never earn.
 *
 * Deliberately NOT used for the demand COLOUR: a market is hot whatever you
 * choose to send, and hiding that would hide the reason to step up a class.
 */
function draftEarnsWrit(multiplier) {
    return earnsDemandWrit(multiplier) && !!OPERATION_TEMPLATES[draft.templateId]?.writEligible;
}

function demandChip(multiplier, { markWrit = true, base = null } = {}) {
    // A fallen price is only readable next to what it fell from, so a fed market
    // carries its struck-through list price. Only when it has actually moved —
    // "×2.00 ×2.00" would be noise on every hungry region on the map.
    const strike = isFed(multiplier, base)
        ? ` <span class="text-gray-500 line-through">×${base.toFixed(2)}</span>` : '';
    if (!multiplier || multiplier <= DEMAND_MIN) {
        return `<span class="text-gray-500">×1.00</span>${strike}`;
    }
    const hot = earnsDemandWrit(multiplier);
    // `markWrit` off wherever the actual Writ count is already on the line —
    // "+2📜 · ×1.90 +📜" says the same thing twice and reads as two bonuses.
    return `<span style="color:${hot ? DEMAND_HOT : GOLD}">×${multiplier.toFixed(2)}${markWrit && draftEarnsWrit(multiplier) ? ' +📜' : ''}</span>${strike}`;
}

/** Plain-text form of the same, for <option> labels and tooltips. */
function demandText(multiplier, base = null) {
    const list = isFed(multiplier, base) ? ` (list ×${base.toFixed(2)})` : '';
    if (!multiplier || multiplier <= DEMAND_MIN) return `×1.00${list}`;
    return `×${multiplier.toFixed(2)}${draftEarnsWrit(multiplier) ? ' +📜' : ''}${list}`;
}

/**
 * Has this price actually been pushed down by saturation?
 *
 * The epsilon is not decoration: both numbers are rounded to 2dp independently,
 * so a market a fraction of a percent fed can round to its own list price and a
 * bare `>` would strike through "×2.00 ×2.00".
 */
function isFed(multiplier, base) {
    return base != null && base > (multiplier || 0) + 0.005;
}

/** Recovery time, rounded to whatever unit reads better at that scale. */
function recoveryText(ms) {
    const hours = ms / 3_600_000;
    return hours >= 1 ? `~${Math.round(hours)}h` : `~${Math.max(1, Math.round(hours * 60))}m`;
}

/**
 * Why a price is down and when it comes back, appended to a tooltip.
 *
 * Empty for a hungry market: the mechanic should be invisible until it bites,
 * and a "0% fed" note on every region teaches nothing.
 */
function saturationNote(regionId, faction) {
    const level = satiationLevel(regionId, faction);
    if (level <= 0) return '';
    return ` · market ${Math.round(level * 100)}% fed, list ×${baseFactionDemand(regionId, faction).toFixed(2)},`
        + ` recovers ${recoveryText(satiationRecoveryMs(regionId, faction))}`;
}

/**
 * Which view of the Operations screen is showing.
 *
 * Planning a convoy and watching three of them are different jobs. Putting both
 * on one page meant the map carried every route at once, which is exactly the
 * information overload this splits apart:
 *
 *   'plan'    — the map with ONLY the route being planned, plus the controls
 *   'transit' — the map with every convoy, plus their cards; no controls
 *
 * The toolbar's "New Operation" button opens 'plan'; the Operations screen tab
 * opens 'transit'.
 */
let view = 'transit';

/** In 'transit', the convoy whose route is isolated. null = show them all. */
let selectedOpId = null;

export function setOperationsView(next) {
    view = next === 'plan' ? 'plan' : 'transit';
    if (view === 'plan') selectedOpId = null;
    renderOperationsTab();
}

export function selectOperation(opId) {
    // Clicking the selected convoy again returns to "show them all", so there
    // is always a way back without hunting for a deselect control.
    selectedOpId = selectedOpId === opId ? null : opId;
    renderOperationsTab();
}

/** Planner selection. Module state, not gameState: nothing here is worth saving. */
const draft = {
    templateId: 'short-haul',
    toRegionId: null,     // the player picks WHERE TO — this is what sets the price
    fromRegionId: null,   // ...and WHERE FROM, which is what sets the clock
    skillId: null,
    leadName: null,
};

/**
 * The four staging points available for the chosen destination, shortest first.
 *
 * No cache and no template argument, both of which the derived-origin version
 * needed: offers were random within a ring, so re-rolling on every render made
 * the map shuffle under the cursor, and reach depended on the template. Neither
 * is true now — this is four Dijkstra runs over 153 tiles with one answer, so
 * caching it would only be a second thing to invalidate.
 *
 * Sorted by duration because duration is the decision the list exists to serve.
 */
function currentOffers() {
    if (!draft.toRegionId) return [];
    return originOffers(draft.toRegionId).sort((a, b) => a.hours - b.hours);
}

/** The offer for the chosen origin, or null. */
function currentOffer() {
    return currentOffers().find(o => o.fromRegionId === draft.fromRegionId) || null;
}

/**
 * Every signature a convoy can carry, grouped by faction.
 *
 * ALL fifteen, not just the player's own five. Restricting cargo to your own
 * faction made the demand premium a single lookup: a dominion player's five
 * signatures all price identically, so the only variable left was destination
 * and there was exactly one right answer forever.
 *
 * Opening it up is what makes this an economy BETWEEN factions rather than a
 * price list. Off-faction material is what a player accumulates by raiding
 * elsewhere and has no recipes for, and the regions that pay most for it are the
 * ones that produce least of it. Nothing had to be built: planCargo already
 * reads any signature, and a signature the player has no stock in simply blocks
 * the launch as "NO CARGO".
 *
 * @returns {Array<{faction:string, name:string, skills:string[]}>}
 */
function signatureGroups() {
    const own = gameState.character?.faction;
    return Object.entries(config.factionSkills || {})
        .map(([faction, def]) => ({
            faction,
            name: def.name || faction,
            skills: Object.keys(def.skills || {}),
        }))
        // The player's own faction first: it is the one they will usually have
        // stock in, so it should not need scrolling to.
        .sort((a, b) => (a.faction === own ? -1 : 0) - (b.faction === own ? -1 : 0));
}

function allSignatureIds() {
    return signatureGroups().flatMap(g => g.skills);
}

/**
 * The cargo-signature control: fifteen signatures in three priced groups.
 *
 * Grouped by faction because the PRICE is per-faction — every dominion signature
 * fetches the same premium at a given destination — so the group header is the
 * honest place for it, and repeating it on all five options would be noise.
 *
 * Each option is marked when the player cannot actually fill that manifest.
 * Without it the only way to discover a signature is unshippable is to select it
 * and watch LAUNCH turn into NO CARGO, which is a dead end per attempt.
 *
 * ⚠ EVERY OPTION CARRIES ITS STOCK, and that is the whole hint. Because the
 * price is per-faction, the five signatures inside a group are worth EXACTLY
 * the same to ship — so the only thing that separates them is how much of each
 * you are sitting on, and the right play is always the deepest pile. Grouping
 * says they are interchangeable; the stock number says which to pick.
 *
 * Without it the choice looks arbitrary and gets made arbitrarily. The
 * simulator did precisely that — it broke the tie on iteration order, shipped
 * the same two signatures forever, and left the biggest piles untouched (the
 * five deepest received 2 convoys between them). A player reading a list of
 * five identically-priced names has no more information than that loop did.
 *
 * Stock is shown in UNITS, not credits: a signature holding three rares
 * outvalues one holding sixty commons, so a credit figure would point at the
 * pile that is not in the player's way. See `signatureStock`.
 *
 * ⚠ THE OPTION SAYS "items" AND THE BUDGET IS IN 💰, and it took three passes.
 * Both figures started as 📦, so one glyph carried two incomparable scales: a
 * Long Haul reading "📦340" beside a signature reading "📦9" invites the
 * conclusion that you are 331 short, when those 9 units are worth 815 — more
 * than twice the budget.
 *
 *   1. "📦340 goods" vs "9 items" — fixed nothing. Synonyms; both read as counts.
 *   2. "📦340 value" vs "9 items" — better, but "value" of WHAT is still a guess.
 *   3. "340💰" vs "9 items" — the budget IS a credit figure (materialValue-core
 *      derives it from the crafting ladder), so it takes the credit icon the
 *      rest of the game already uses. A count and a price cannot be confused.
 *
 * The numbers were right throughout; only their units were ever missing.
 *
 * Sorted deepest-first WITHIN a group for the same reason — the useful answer
 * should not need hunting for. Groups keep their own order (the player's own
 * faction leads), because that is a different question.
 */
function signatureSelect(templateId, sizeClass = 'text-sm px-2 py-1.5') {
    const groups = signatureGroups().map(group => {
        const premium = draft.toRegionId
            ? ` · ${demandText(factionDemandMultiplier(draft.toRegionId, group.faction),
                               baseFactionDemand(draft.toRegionId, group.faction))}` : '';
        const options = group.skills
            .map(skillId => ({ skillId, stock: signatureStock(skillId).units }))
            .sort((a, b) => b.stock - a.stock)
            .map(({ skillId, stock }) => {
                const { short } = planCargo(templateId, skillId);
                const thin = short.value > 0;
                const name = config.factionSkills[group.faction]?.skills?.[skillId]?.name || skillId;
                return `<option value="${skillId}" ${skillId === draft.skillId ? 'selected' : ''}>${name} · ${stock} items${thin ? ' ⚠' : ''}</option>`;
            }).join('');
        return `<optgroup label="${group.name}${premium}">${options}</optgroup>`;
    }).join('');

    return `
        <select id="op-skill" onchange="setOperationSignature(this.value)"
            title="Every signature in a faction fetches the SAME price, so the only thing separating them is stock — 📦 is what the dock holds. Ship your deepest pile."
            class="w-full bg-white/5 border-2 border-gray-600 rounded text-white ${sizeClass}">
            ${groups}
        </select>`;
}

/**
 * Fill any unset draft field with a sensible default.
 *
 * Endpoints are deliberately NOT defaulted: the map lets the player pick them,
 * and pre-filling both was what made pin-clicking feel broken — with origin and
 * destination always set, every click could only ever overwrite the
 * destination, so there was no way to choose an origin or start over.
 */
function ensureDraftDefaults() {
    if (!draft.skillId) draft.skillId = allSignatureIds()[0] || null;
    // A destination with no staging point is a half-planned route the player
    // cannot launch and did not ask for. Default to the SHORTEST run: it is the
    // cheapest thing that can go wrong, and the long routes are the ones worth
    // choosing deliberately. Also re-seats a stale origin — picking a new
    // destination can make the previous origin the destination itself.
    const offers = currentOffers();
    if (offers.length && !offers.some(o => o.fromRegionId === draft.fromRegionId)) {
        draft.fromRegionId = offers[0].fromRegionId;
    }
    if (!draft.toRegionId) draft.fromRegionId = null;
    const leads = availableLeads();
    if (!draft.leadName || !leads.some(l => l.name === draft.leadName)) {
        // BEST record, not the first in the list. The lead bonus is worth up to
        // +25% credits, so defaulting to whoever happened to be first was
        // quietly costing the player money on every convoy they did not stop to
        // re-pick — and it is what lets the control be small.
        draft.leadName = leads.reduce((best, l) =>
            (!best || (l.marksEarned || 0) > (best.marksEarned || 0)) ? l : best, null)?.name || null;
    }
}

// ---------------------------------------------------------------------------
// Map
// ---------------------------------------------------------------------------

function renderMap() {
    const container = document.getElementById('operations-map');
    if (!container) return;
    const { width, height, tiles, regions } = getWorldGrid();
    // One timestamp for the whole paint: convoy progress, the terminator and
    // every tooltip must agree about when "now" is.
    const now = Date.now();

    // The planned route belongs to the PLANNER only. Drawing it on the transit
    // board left the last-planned route highlighted there permanently — and
    // because it never cleared when a convoy was selected, it read as one
    // convoy's highlight refusing to go away.
    const offer = view === 'plan' ? currentOffer() : null;
    const planned = offer ? { tiles: offer.tiles, cost: offer.cost } : null;
    const plannedKeys = new Set((planned?.tiles || []).map(t => `${t.row},${t.col}`));

    // The planner shows ONLY the route being planned — that is the whole point
    // of splitting the views. The transit board shows every convoy, or just one
    // when the player has selected it.
    const allOps = gameState.syndicate?.operations || [];
    const shown = view === 'plan' ? []
        : selectedOpId ? allOps.filter(o => o.id === selectedOpId)
        : allOps;

    // tile -> { color, fill } where `fill` is how far this convoy has crossed
    // THIS tile, 0..1. Split by TERRAIN COST via routeProgress(), so a water
    // tile holds the convoy three times as long as an open one — which is what
    // the route cost already charged for in the duration.
    const convoyAt = new Map();
    const convoys = shown.map((op) => {
        const color = CONVOY_COLORS[allOps.indexOf(op) % CONVOY_COLORS.length];
        const route = op.waypoints ? routeFromWaypoints(op.waypoints)
            : findRoute(op.from || op.fromRegionId, op.toRegionId);
        const routeTiles = route?.tiles || [];
        const pct = Math.min(1, Math.max(0, (now - op.startedAt) / (op.completesAt - op.startedAt)));
        const { fills, index } = routeProgress(route, pct);

        routeTiles.forEach((t, i) => {
            const key = `${t.row},${t.col}`;
            const fill = fills[i] ?? 0;
            const existing = convoyAt.get(key);
            // Where two routes overlap, the further-advanced one wins the tile,
            // so a shared corridor still shows the leading convoy.
            if (!existing || fill > existing.fill) convoyAt.set(key, { color, fill });
        });

        // Same computation drives the marker, so the ▪ can never disagree with
        // the squares about where the convoy is.
        return { op, color, at: routeTiles[index], pct };
    });
    const convoyMarker = new Map(convoys.filter(c => c.at).map(c => [`${c.at.row},${c.at.col}`, c]));

    // Leg boundaries. The first waypoint already carries the staging marker (the
    // class icon) and the last is the 🏕️ destination, so only the joins in
    // between get a flag — those are the turns a tour actually makes.
    const legFlagAt = new Map();
    const flagWaypoints = (waypoints, color) => {
        (waypoints || []).slice(1, -1).forEach(w => {
            const key = `${w.row},${w.col}`;
            if (!legFlagAt.has(key)) legFlagAt.set(key, color);
        });
    };
    if (offer) flagWaypoints(offer.waypoints, GOLD);
    convoys.forEach(c => flagWaypoints(c.op.waypoints, c.color));

    const capitalOf = {};
    for (const id of operationRegions()) {
        const t = regionTile(id);
        if (t) capitalOf[`${t.row},${t.col}`] = id;
    }

    let cells = '';
    for (let row = 0; row < height; row++) {
        for (let col = 0; col < width; col++) {
            const terrain = tiles[row][col];
            const regionId = regions[row][col];
            const style = TERRAIN_STYLE[terrain] || TERRAIN_STYLE['.'];
            const bg = mixHex(REGION_COLOR[regionId] || '#333333', style.tint, style.mix);
            const key = `${row},${col}`;
            const capital = capitalOf[key];
            const onRoute = plannedKeys.has(key);
            const convoy = convoyAt.get(key);
            const convoyColor = convoy?.color;
            const marker = convoyMarker.get(key);

            const regionName = config.raidRegions?.[regionId]?.name || regionId || 'Unclaimed';
            // A capital is a place you can SELL to, so its tooltip carries the
            // price. Only in the planner: on the transit board the signature is
            // already fixed per convoy and a hover price would be misleading.
            const capitalDemand = capital && view === 'plan' && draft.skillId
                ? ` · pays ${demandText(demandMultiplier(capital, draft.skillId))}`
                  + saturationNote(capital, getSkillFaction(draft.skillId)) : '';
            // Local time on EVERY tile, not just capitals: the clock is keyed
            // to tiles, the terminator is visible across open ground, and a
            // player who wonders why one stretch of a route is dark should get
            // the answer by hovering it.
            const tileLocal = getTilePhase({ row, col }, now);
            const title = `${regionName} · ${style.label}${style.cost ? ` (cost ${style.cost})` : ''}${capitalDemand} · ${tileLocal.time} local`;

            // Origin and destination are DIFFERENT glyphs, not the same pin in a
            // different colour — direction has to be readable at a glance.
            //
            // In the planner EVERY capital is a possible destination, so every
            // capital wears the 🏕️ and carries its own price. The premium was
            // otherwise only visible one option at a time in a dropdown, which
            // is the worst possible place for a number whose entire purpose is
            // to be compared against four others.
            let glyph = '';
            const isOrigin = offer && offer.from.row === row && offer.from.col === col;
            const legFlag = legFlagAt.get(key);
            const chosen = capital && capital === draft.toRegionId;
            if (chosen) glyph = '<span class="world-pin dest" title="Destination">🏕️</span>';
            // Origin beats the generic capital marker: a tour can stage from a
            // capital, and there the direction matters more than the price.
            //
            // It wears the CLASS icon rather than a generic ▶, so the map states
            // what is being sent as well as where from. Safe to read the draft
            // here: `offer` is null outside the planner (see its definition), so
            // isOrigin is only ever true while this draft is the thing on screen.
            else if (isOrigin) {
                const cls = OPERATION_TEMPLATES[draft.templateId];
                glyph = `<span class="world-pin origin" title="Staging point${cls ? ` — ${cls.name}` : ''}">${cls?.icon || '▶'}</span>`;
            }
            else if (legFlag) glyph = `<span class="world-pin leg" style="color:${legFlag}" title="Leg staging point">⚑</span>`;
            else if (capital && view === 'plan') glyph = '<span class="world-pin dest dim" title="Possible destination">🏕️</span>';
            else if (capital) glyph = '<span class="world-pin">◆</span>';

            // Price tag, planner only. On the transit board each convoy's
            // signature is already fixed, so a per-region price there would be
            // quoting a trade the player can no longer make.
            if (capital && view === 'plan' && draft.skillId && !isOrigin) {
                const mult = demandMultiplier(capital, draft.skillId);
                const hot = earnsDemandWrit(mult);
                const color = mult <= DEMAND_MIN ? '#9ca3af' : (hot ? DEMAND_HOT : GOLD);
                // One line, not two: a tile is ~40px wide and a wrapped price tag
                // spills past its own tile into the neighbour's.
                // The 📜 tracks the DRAFT, not just the market — the colour still
                // says "hungry" so stepping up a class has a visible reason.
                glyph += `<span class="world-price" style="color:${color}">×${mult.toFixed(2)}${draftEarnsWrit(mult) ? '📜' : ''}</span>`;
            }
            // Progress square: grows 0 -> 50% of the tile as the convoy crosses it,
            // so a route reads as a filling bar rather than a static outline.
            if (convoy && convoy.fill > 0) {
                const size = (convoy.fill * 50).toFixed(1);
                glyph += `<span class="world-progress" style="width:${size}%;height:${size}%;background:${convoy.color}"></span>`;
            }
            if (marker) glyph += `<span class="world-convoy" style="color:${marker.color}">▪</span>`;

            cells += `<div class="world-tile${capital ? ' capital' : ''}${onRoute ? ' on-route' : ''}${convoyColor && !onRoute ? ' on-active' : ''}"
                style="background:${bg}; --night:var(--n${col})${convoyColor && !onRoute ? `; outline-color:${convoyColor}` : ''}" title="${title}"
                ${capital ? `data-region="${capital}" onclick="cycleOperationEndpoint('${capital}')"` : ''}>${glyph}</div>`;
        }
    }

    // In the planner the legend doubles as the PRICE LIST: five destinations,
    // five premiums, side by side. Comparing them is the whole decision, and a
    // dropdown you have to open one option at a time cannot show a comparison.
    const legend = operationRegions().map(id => {
        const name = config.raidRegions?.[id]?.name || id;
        const role = id === draft.toRegionId ? '🏕️ destination' : '';
        // The legend is the price LIST — five destinations side by side — so it
        // is where the list price belongs next to the fed one. On the map itself
        // a struck-through second number would spill out of a 40px tile.
        const price = view === 'plan' && draft.skillId
            ? ` ${demandChip(demandMultiplier(id, draft.skillId),
                             { base: baseFactionDemand(id, getSkillFaction(draft.skillId)) })}` : '';
        // The legend is already the map's key, and it is listed west to east —
        // which makes it the one place five local clocks can be read in order
        // and the rotation seen as a sequence rather than a shading.
        const local = getRegionPhase(id, now);
        return `<span class="text-xs inline-flex items-center gap-1 ${role ? 'font-bold' : 'text-gray-400'}" style="${role ? `color:${GOLD}` : ''}">
            <span style="display:inline-block;width:9px;height:9px;border-radius:2px;background:${REGION_COLOR[id]}"></span>
            ${name}${role ? ` (${role})` : ''}${price}<span class="world-clock-chip" data-clock-region="${id}" title="${name} local time — ${local.name}">${local.icon} ${local.time}</span></span>`;
    }).join('<span class="text-gray-600 text-xs">·</span> ');

    // The legend belongs directly under the map it explains, in the same column.
    // It sat outside the row for a while so the controls column could align to
    // the map's bottom edge; collapsing the three template cards into one select
    // made the columns comparable again without that trick.
    container.innerHTML = `
        <div class="world-map" style="grid-template-columns: repeat(${width}, minmax(0, 1fr));">${cells}</div>
        <div class="world-map-legend mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">${legend}</div>`;
    paintWorldClock(now);
}

/**
 * Advance the terminator across the world map (M3 S9).
 *
 * Every tile has its own clock, but longitude does not depend on latitude — so
 * a column's worth of tiles always share a phase, and the paint is DEDUPED BY
 * COLUMN rather than computed 153 times. Writes one `--n<col>` variable onto
 * the `.world-map` element; each tile reads its column's through `--night` (see
 * styles.css). Seventeen property writes, no reflow of the grid, and —
 * critically — no innerHTML: this runs on the planner too, where rebuilding the
 * map is fine but rebuilding anything else would close an open <select> under
 * the player's cursor.
 *
 * The darkness is the inverse of the daylight curve, capped below 1 so a night
 * tile is still readable: this is a world clock, not a fog of war, and a player
 * has to be able to plan a route across the dark side.
 */
const MAX_NIGHT = 0.62;

function paintWorldClock(now = Date.now()) {
    const map = document.querySelector('#operations-map .world-map');
    if (!map) return;
    const { width } = getWorldGrid();
    for (let col = 0; col < width; col++) {
        const night = (1 - daylightFraction(getTilePhase({ row: 0, col }, now).phase)) * MAX_NIGHT;
        map.style.setProperty(`--n${col}`, night.toFixed(3));
    }
    // The legend's clocks are restated in place rather than re-rendered with
    // the legend, for the same reason: this has to be safe to run on the
    // planner while a dropdown is open.
    for (const chip of document.querySelectorAll('#operations-map [data-clock-region]')) {
        const local = getRegionPhase(chip.dataset.clockRegion, now);
        chip.textContent = `${local.icon} ${local.time}`;
    }
}

/**
 * Tick entry point for the world clock, called from the 5s loop in main.js.
 *
 * A game day is 10 real minutes, so 5s advances the world by ~12 minutes —
 * the terminator crawls rather than jumps. Costs nothing when the player is
 * anywhere else: the tab check is the first thing it does.
 */
export function tickWorldClock() {
    const tab = document.getElementById('operations-tab');
    if (!tab?.classList.contains('active')) return;
    paintWorldClock();
}

/**
 * Pick endpoints by clicking the map: first click sets the ORIGIN, second sets
 * the DESTINATION, and a third starts over from that region.
 *
 * The previous version tried to be clever — cycling origin/destination on every
 * click — but both endpoints were always pre-filled, so in practice a click
 * could only ever overwrite the destination and there was no way to clear.
 * "First click, second click, then restart" needs no explanation and always has
 * a way out. Clicking the current origin clears it, so a mis-click is one click
 * to undo.
 */
/**
 * Map clicks now have to name TWO endpoints, from one gesture.
 *
 * The rule: an unset capital becomes the destination if there isn't one, and
 * the staging point otherwise; clicking either of the two clears that one.
 * Destination first because it is the decision that matters more — it sets the
 * price, and `ensureDraftDefaults` fills a sensible origin behind it, so one
 * click still produces a launchable route the way it always did.
 */
export function cycleOperationEndpoint(regionId) {
    if (draft.toRegionId === regionId) draft.toRegionId = null;
    else if (draft.fromRegionId === regionId) draft.fromRegionId = null;
    else if (!draft.toRegionId) draft.toRegionId = regionId;
    else draft.fromRegionId = regionId;
    renderOperationsTab();
}

export function setOperationOrigin(regionId) {
    draft.fromRegionId = regionId || null;
    renderOperationsTab();
}

export function clearOperationRoute() {
    draft.toRegionId = null;
    draft.fromRegionId = null;
    renderOperationsTab();
}

// ---------------------------------------------------------------------------
// In-flight
// ---------------------------------------------------------------------------

function formatRemaining(ms) {
    if (ms <= 0) return 'arriving';
    const totalMinutes = Math.ceil(ms / 60000);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/**
 * The convoy list, as a column beside the map.
 *
 * Stacked vertically in a ~224px column rather than a wide grid, so each card
 * has to earn its lines: route, lead, progress, and what it pays. The cargo
 * manifest was dropped from the card — it is fixed at launch and unchangeable
 * in transit, so on this screen it is history, not information. What a player
 * comes here for is where a convoy IS and when it lands.
 */
function renderInFlight() {
    const container = document.getElementById('operations-inflight');
    if (!container) return;
    const ops = gameState.syndicate?.operations || [];
    const slots = operationSlots();

    if (!ops.length) {
        container.innerHTML = `<div class="text-xs text-gray-400">No convoys in transit · ${slots} slot${slots === 1 ? '' : 's'} licensed</div>`;
        return;
    }

    const now = Date.now();
    container.innerHTML = `
        <div class="text-[10px] uppercase tracking-widest text-gray-400 mb-1">
            In transit — ${ops.length}/${slots}${selectedOpId ? ' · one route' : ''}</div>
        <div class="flex flex-col gap-1.5">
        ${ops.map(op => {
            const t = OPERATION_TEMPLATES[op.templateId];
            const total = op.completesAt - op.startedAt;
            const pct = Math.min(100, Math.round(((now - op.startedAt) / total) * 100));
            const payout = operationYield(op);
            const from = config.raidRegions?.[op.fromRegionId]?.name || op.fromRegionId;
            const to = config.raidRegions?.[op.toRegionId]?.name || op.toRegionId;
            const color = CONVOY_COLORS[ops.indexOf(op) % CONVOY_COLORS.length];
            const isSelected = selectedOpId === op.id;
            return `
                <div onclick="selectOperation('${op.id}')"
                    class="rounded-lg px-2 py-1.5 cursor-pointer text-[11px] leading-tight"
                    style="background:rgba(224,168,76,${isSelected ? '0.16' : '0.07'}); border:2px solid ${isSelected ? color : GOLD_DIM};"
                    title="${from} → ${to}${isSelected ? ' · click again to show every route' : ' · click to show only this route'}">
                    <div class="font-bold text-xs flex items-center gap-1 truncate" style="color:${GOLD}">
                        <span style="color:${color}">▪</span>${t?.icon || '🚚'} ${t?.name || op.templateId}</div>
                    <div class="text-gray-300 truncate">${from} → ${to}</div>
                    <div class="text-gray-400 truncate">${op.leadName}</div>
                    <div class="mt-1 h-1.5 rounded-full bg-black/40 overflow-hidden">
                        <div class="h-full" style="width:${pct}%; background:${GOLD}"></div>
                    </div>
                    <div class="text-gray-400 flex justify-between gap-1 whitespace-nowrap mt-0.5">
                        <span>${formatRemaining(op.completesAt - now)}</span>
                        <span>${payout.credits.toLocaleString()}💰${payout.writs ? ` +${payout.writs}📜` : ''}</span>
                    </div>
                </div>`;
        }).join('')}
        </div>`;
}

// ---------------------------------------------------------------------------
// Planner
// ---------------------------------------------------------------------------

/**
 * Short button faces. "Ready to launch" above an enabled LAUNCH button said
 * nothing the button did not already say, so the state now lives IN the button
 * — a disabled control that explains itself beats a disabled control plus a
 * caption. The full sentence stays as the tooltip.
 */
const BLOCKED_LABEL = {
    'no-route': 'NO ROUTE',
    'insufficient-cargo': 'NO CARGO',
    'insufficient-energy': 'NO ENERGY',
    'no-free-slot': 'NO SLOT',
    'lead-unavailable': 'NO LEAD',
    'insufficient-legends': 'LOCKED',
    'no-charter': 'UNLICENSED',
};

const BLOCKED_COPY = {
    'no-charter': 'Sign the Operations Charter first.',
    'no-free-slot': 'Every licensed slot is busy.',
    'insufficient-legends': 'Promote more Legends this era to run longer routes.',
    'lead-unavailable': 'No idle lead — the Leader and every era-Legend is out.',
    'no-route': 'No route between those regions.',
    'insufficient-cargo': 'Not enough material in that signature.',
    'insufficient-energy': 'Not enough energy.',
    'unknown-template': 'Unknown operation type.',
};

/**
 * Shared planner state, computed once per paint.
 *
 * The controls column and the manifest row both need the offer, the leads and
 * the eligibility verdict. Computing them twice would be two chances for the
 * quoted price and the LAUNCH button to disagree about the same convoy.
 */
function plannerContext() {
    ensureDraftDefaults();
    const offer = currentOffer();
    const leads = availableLeads();
    return {
        offer,
        leads,
        eligible: canLaunchOperation({
            templateId: draft.templateId, fromRegionId: draft.fromRegionId,
            toRegionId: draft.toRegionId, skillId: draft.skillId, leadName: draft.leadName,
        }),
    };
}

/**
 * The column beside the map: what SIZE of operation, who leads it, what it
 * carries, and LAUNCH at the bottom.
 *
 * Vertical because it sits next to a map that wants every pixel of width, and
 * because these are the sizing decision — read top to bottom, they are the order
 * the question is actually asked. Destination and staging point are deliberately
 * NOT here: the map sets the destination by click and prices every candidate on
 * the tile itself, and the staging point is derived from it, so both were form
 * fields restating what the map already showed.
 */
function renderControls() {
    const container = document.getElementById('operations-controls');
    if (!container) return;
    const { offer, leads, eligible } = plannerContext();
    const eraLegends = gameState.command?.legends?.length || 0;

    // ONE select, not three cards. Stacked cards were 171px of a column that has
    // to fit beside the map, and they showed three sets of numbers when only the
    // selected one is being decided about. The cost line below restates that one
    // set, so nothing is lost — the other two are a dropdown away.
    // A class is priced in CARGO, not hours. It used to show the hours its own
    // reach implied, which no longer means anything: every class runs the same
    // route in the same time, and what separates them is how much they carry.
    const templateOptions = Object.entries(OPERATION_TEMPLATES).map(([id, t]) => {
        const locked = eraLegends < t.legendsRequired;
        const suffix = locked
            ? ` — 🔒 ${t.legendsRequired} Legends`
            : ` · ${t.cargoValue}💰📦`;
        return `<option value="${id}" ${locked ? 'disabled' : ''} ${id === draft.templateId ? 'selected' : ''}>${t.icon} ${t.name}${suffix}</option>`;
    }).join('');

    // COSTS ONLY. A Writ is never spent to launch anything — `writEligible`
    // gates the demand trickle a convoy EARNS — but sitting on this line, under
    // a tooltip that opens "Energy drawn", it read as a third price, and the
    // Writ paid back on delivery then looked like a refund of it. The 📜 now
    // appears only where it is genuinely earned: on the destinations that pay
    // it (map and legend, gated by draftEarnsWrit) and on the payout line.
    const selected = OPERATION_TEMPLATES[draft.templateId];
    const costLine = selected
        ? `${energyFor(draft.templateId)}⚡ · ${selected.cargoValue}💰📦`
        : '';

    // The staging point is the DURATION dial, and the reason it is a control
    // rather than a derived tile: cargo is flat per class, so a longer run is
    // the same manifest for more credits. Each option carries its own hours
    // because that number IS the choice being made.
    const originOptions = currentOffers().map(o => {
        const name = config.raidRegions?.[o.fromRegionId]?.name || o.fromRegionId;
        return `<option value="${o.fromRegionId}" ${o.fromRegionId === draft.fromRegionId ? 'selected' : ''}>${name} · ${o.hours.toFixed(1)}h</option>`;
    }).join('');
    const originSelect = originOptions
        ? `<select id="op-origin" onchange="setOperationOrigin(this.value)"
                class="w-full bg-white/5 border-2 border-gray-600 rounded px-2 py-1 text-xs text-white">${originOptions}</select>`
        : '<div class="text-xs text-gray-400">Pick a destination first</div>';

    const leadSelect = leads.length
        ? `<select id="op-lead" onchange="setOperationLead(this.value)"
                class="w-full bg-white/5 border-2 border-gray-600 rounded px-2 py-1 text-xs text-white">
                ${leads.map(l => `<option value="${l.name}" ${l.name === draft.leadName ? 'selected' : ''}>${l.name}${l.isLeader ? ' (Leader)' : ''} · ${l.marksEarned}🎖️</option>`).join('')}
           </select>`
        : '<div class="text-xs text-gray-400">No idle lead</div>';

    // The route, immediately above the button that commits it — the last thing
    // read before pressing LAUNCH should be what is being launched.
    const routeBlock = offer
        ? `<div class="text-xs leading-tight rounded px-2 py-1 mt-1 2xl:mt-2 flex" style="background:rgba(0,0,0,0.25); border:1px solid ${GOLD_DIM};">
               <div class="flex-grow">
                    <div class="text-gray-300 truncate" title="Staging point">▶ ${config.raidRegions?.[offer.fromRegionId]?.name || offer.fromRegionId}</div>
                    <div class="font-bold truncate" style="color:${GOLD}" title="Destination">🏕️ ${config.raidRegions?.[draft.toRegionId]?.name || draft.toRegionId}</div>
                </div>
               <div class="text-gray-400">${offer.hours.toFixed(1)}h</div>
           </div>`
        : `<div class="text-xs text-gray-400 leading-snug mt-1">Click a destination on the map.</div>`;

    container.innerHTML = `
        <div class="text-[11px] uppercase tracking-widest text-gray-300 mb-0.5">Operation</div>
        <select id="op-template" onchange="selectOperationTemplate(this.value)"
            class="w-full bg-white/5 border-2 rounded px-2 py-1.5 text-sm font-bold text-white"
            style="border-color:${GOLD}">${templateOptions}</select>
        <!-- The 💰 figure is what the CARGO must be worth, not a charge against
             the credit balance — a convoy is paid for in materials off the dock.
             Spelling that out because the icon says credits and the line sits
             beside an energy cost that IS deducted. -->

        <div class="mt-2">
            <div class="text-[11px] uppercase tracking-widest text-gray-300 mb-0.5">Operation Cost</div>

            <div class="text-xs text-gray-400 mt-0.5"
                title="What this convoy costs to launch: energy drawn · credits' worth of materials the manifest must carry (taken from the Guild Vault's dock, never from your credit balance). No Writs are spent — a Grand Circuit EARNS one by delivering into a hungry market.">${costLine}
            </div>
        </div>

        <div class="mt-2">
            <div class="text-[11px] uppercase tracking-widest text-gray-300 mb-0.5">Cargo signature</div>
            ${signatureSelect(draft.templateId, 'text-xs px-2 py-1')}
        </div>

        <div class="mt-2">
            <div class="text-[11px] uppercase tracking-widest text-gray-300 mb-0.5"
                 title="Where the convoy forms up. Cargo is the same whatever the distance, so a longer run is more credits for the same manifest — and a longer hold on the slot.">Staging point</div>
            ${originSelect}
        </div>

        <div class="mt-2 mb-2">
            <!-- Lead is worth up to +25% credits (LEAD_BONUS_CAP), so it is not
                 decoration — but ensureDraftDefaults picks the best available
                 record, so the default needs no attention and the control can be
                 the smallest thing here. -->
            <div class="text-[11px] uppercase tracking-widest text-gray-300 mb-0.5">Lead</div>
            ${leadSelect}
        </div>

        ${routeBlock}
        ${manifestFragment(offer, leads)}

        <button onclick="launchOperation()" ${eligible.ok ? '' : 'disabled'}
            title="${eligible.ok ? '' : (BLOCKED_COPY[eligible.reason] || 'Cannot launch')}"
            class="w-full mt-1.5 2xl:mt-4 px-3 py-2.5 rounded font-bold ${eligible.ok ? 'text-black hover:shadow-lg' : 'bg-gray-600 text-gray-800 cursor-not-allowed'}"
            ${eligible.ok ? `style="background:${GOLD}"` : ''}>${eligible.ok ? 'LAUNCH' : (BLOCKED_LABEL[eligible.reason] || 'CANNOT LAUNCH')}</button>

        <!-- Last in the column, so its bottom edge lands on the map's. Undo
             belongs after the commit control, not competing with it. -->
        <button onclick="clearOperationRoute()" ${draft.toRegionId ? '' : 'disabled'}
            class="w-full mt-1.5 2xl:mt-2 px-3 py-1 rounded text-xs font-bold ${draft.toRegionId ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-800 text-gray-600 cursor-not-allowed'}">Clear route</button>`;
}

/**
 * The manifest and payout, as a fragment for the controls column.
 *
 * Returns HTML rather than rendering, because it now lives INSIDE the column
 * between the route and LAUNCH — cost and reward belong next to the button that
 * commits them, and below the map they were a second place to look after the
 * decision had already been made.
 */
function manifestFragment(offer, leads) {
    // Flat per class, so the manifest does not move when the staging point does
    // — which is exactly the trade the planner is trying to make visible: the
    // rows below stay put while the payout above them climbs with distance.
    const { manifest, short, value, budget } = draft.skillId
        ? planCargo(draft.templateId, draft.skillId)
        : { manifest: {}, short: { value: 0 }, value: 0, budget: 0 };

    // A convoy that could not fill its budget without going OVER it sails light,
    // and silently doing so is what made the planner read as broken: a Long Haul
    // asking 340 value, a signature holding 9 items, and a manifest taking two
    // chips gave the player three numbers and no way to reconcile them. The
    // header now states what was actually loaded against what was asked, and
    // amber marks the gap so "240/340" is not mistaken for a rounding artefact.
    const underFilled = budget > 0 && value < budget;

    // One line per item, each saying what the pile will be left at. "-80" alone
    // answers what this convoy costs but not whether the next one is
    // affordable, which is the question a player actually has. The stock is the
    // Guild Vault's dock — account-level, so no character owns it.
    const inventory = operationStores();
    const manifestRows = Object.entries(manifest)
        .map(([id, qty]) => {
            const held = inventory[id] || 0;
            const left = Math.max(0, held - qty);
            // Amber when the convoy takes most of what is there — a pile about
            // to run dry is worth noticing before pressing LAUNCH.
            const thin = held > 0 && left / held < 0.25;
            return `<div class="flex justify-between gap-1 leading-tight">
                <span class="truncate" title="${getItemName(id) || id}">${getItemName(id) || id}</span>
                <span class="whitespace-nowrap"><span class="text-gray-400">-${qty}</span><span class="${thin ? 'text-amber-300' : 'text-gray-500'}">/${left.toLocaleString()}</span></span>
            </div>`;
        })
        .join('');

    // Projected payout, computed by the SAME function that pays it out. A
    // planner that estimates with its own arithmetic is a planner that will one
    // day quote a number the settlement does not honour.
    const projected = offer && draft.toRegionId ? operationYield({
        templateId: draft.templateId,
        hours: offer.hours,
        toRegionId: draft.toRegionId,
        skillId: draft.skillId,
        leadMarks: leads.find(l => l.name === draft.leadName)?.marksEarned || 0,
    }) : null;

    if (!manifestRows && !projected) return '';

    return `
        <div class="mt-2 rounded px-2 py-1 text-[11px]" style="background:rgba(0,0,0,0.25); border:1px solid ${GOLD_DIM};">
            <div class="flex justify-between items-baseline gap-1 text-[11px] uppercase tracking-widest text-gray-400"
                 title="Cargo is loaded from the Guild Vault — the share of each promoted member's kit their successor did not inherit. No character owns it.${underFilled ? `\n\nLoaded ${value}💰 of the ${budget}💰 this class asks for. Nothing left on the dock fits the remaining ${budget - value} without going over, so the convoy sails a little light rather than overpaying.` : ''}">
                <span>🏦 Manifest</span>
                ${budget ? `<span class="${underFilled ? 'text-amber-300' : 'text-gray-500'} normal-case tracking-normal">${value}/${budget}💰</span>` : ''}
            </div>
            ${manifestRows || '<div class="text-gray-400 leading-tight">Pick a destination</div>'}
            ${short.value
                ? `<div class="text-red-300 leading-tight">Short ${short.value}💰</div>`
                : ''}
            ${projected
                // "Payout", not "On arrival": one word, and it left room for the
                // figures and the premium to share the line instead of wrapping
                // onto three.
                ? `<div class="flex justify-between items-baseline gap-1 mt-1 pt-1 whitespace-nowrap" style="border-top:1px solid ${GOLD_DIM}"
                        title="Demand premium: ${config.raidRegions?.[draft.toRegionId]?.name || draft.toRegionId} pays ${demandText(projected.demand)} for ${config.factionSkills?.[getSkillFaction(draft.skillId)]?.name || 'this'} cargo${saturationNote(draft.toRegionId, getSkillFaction(draft.skillId))}">
                     <span class="text-[11px] uppercase tracking-widest text-gray-400">Payout</span>
                     <span><span class="font-bold" style="color:${GOLD}">${projected.credits.toLocaleString()}💰</span>${projected.writs ? `<span class="font-bold" style="color:${GOLD}"> +${projected.writs}📜</span>` : ''} ${demandChip(projected.demand, { markWrit: false, base: baseFactionDemand(draft.toRegionId, getSkillFaction(draft.skillId)) })}</span>
                   </div>`
                : ''}
        </div>`;
}

export function renderOperationsTab() {
    const inflight = document.getElementById('operations-inflight');
    const heading = document.getElementById('operations-heading');

    if (getCharterRank('operations-charter') <= 0) {
        const controls = document.getElementById('operations-controls');
        if (controls) controls.innerHTML = '<div class="text-sm text-gray-400">Sign the Operations Charter to license convoys.</div>';
        return;
    }
    // Defaults FIRST: the map highlights the planned route, and it renders
    // before the controls. Filling the draft inside renderControls meant the map
    // drew with no endpoints on the very first paint.
    ensureDraftDefaults();

    const planning = view === 'plan';
    if (heading) {
        // One line each. The subtitles were flavour, and on the planner the
        // whole screen is the explanation.
        heading.innerHTML = planning
            ? `<h3 class="text-lg font-bold flex flex-wrap items-baseline gap-x-2" style="color:${GOLD}">Plan an Operation
                 <span class="text-xs font-normal text-gray-400">Cargo is spent at launch — it does not come back.</span></h3>`
            : `<h3 class="text-lg font-bold flex flex-wrap items-baseline gap-x-2" style="color:${GOLD}">Operations in Transit
                 <span class="text-xs font-normal text-gray-400">Click a convoy to isolate its route.</span></h3>`;
    }

    // Each view shows exactly one job's worth of controls: the planner has no
    // convoy list, the transit board has no planning fields or launch button.
    const controls = document.getElementById('operations-controls');
    if (controls) controls.classList.toggle('hidden', !planning);
    if (inflight) inflight.classList.toggle('hidden', planning);

    renderMap();
    if (planning) renderControls(); else renderInFlight();
}

// --- planner setters (onclick/onchange string handlers) ---
export function selectOperationTemplate(id) { draft.templateId = id; renderOperationsTab(); }
export function setOperationDestination(id) { draft.toRegionId = id || null; renderOperationsTab(); }
export function setOperationSignature(id) { draft.skillId = id; renderOperationsTab(); }
export function setOperationLead(name) { draft.leadName = name; renderOperationsTab(); }

export function launchOperation() {
    const result = launchOperationCore({
        templateId: draft.templateId, fromRegionId: draft.fromRegionId,
        toRegionId: draft.toRegionId, skillId: draft.skillId, leadName: draft.leadName,
    });
    if (!result.success) {
        addNotification(BLOCKED_COPY[result.reason] || 'Could not launch that operation', 'error');
        return;
    }
    const t = OPERATION_TEMPLATES[result.operation.templateId];
    addNotification(`${t.icon} ${t.name} away — ${result.hours.toFixed(1)}h, led by ${result.operation.leadName}`, 'success');
    saveGameState();
    // Hand straight off to the transit board with the new convoy isolated: the
    // player just answered "what shall I send?", so the next useful question is
    // "where is it?" — not the empty planner they were already looking at.
    view = 'transit';
    selectedOpId = result.operation.id;
    renderOperationsTab();
    updateUI();
}

/**
 * Settle anything that has arrived. Called from the master tick.
 *
 * `maxPerSlot` is left unbounded here on purpose: while the client is running,
 * every operation had to be launched by hand, so there is nothing to cap. The
 * cap belongs to the LOAD path (settleOperationsOnLoad), which is where an
 * absence turns into unearned rounds.
 */
export function settleOperationsTick() {
    const hadOps = (gameState.syndicate?.operations || []).length > 0;
    const result = hadOps ? settleOperationsCore({ now: Date.now() }) : { settled: [] };

    if (result.settled.length) {
        announceSettlement(result);
        saveGameState();
        updateUI();
    }

    // Countdowns, progress bars and the map's per-tile fill are all functions
    // of NOW, so they have to be repainted on the tick — not only when the
    // player happens to click something. Only while the screen is actually
    // showing, so this costs nothing from anywhere else in the game.
    if (hadOps || result.settled.length) refreshOperationsIfVisible();
}

/**
 * Repaint the transit board if it is the one on display.
 *
 * TRANSIT ONLY. The planner holds live <select> elements, and rebuilding it
 * under the player would close an open dropdown mid-choice — the same failure
 * the toolbar hit with the energy popup. It also has nothing to repaint:
 * route duration and cargo do not change with the clock.
 */
function refreshOperationsIfVisible() {
    if (view !== 'transit') return;
    const tab = document.getElementById('operations-tab');
    if (tab?.classList.contains('active')) renderOperationsTab();
}

/**
 * Settle on load, capped at ONE ROUND PER SLOT.
 *
 * A player away for a week must collect the same as one away overnight. The cap
 * lives in the settlement path rather than the launcher so the invariant holds
 * however L4's auto-launcher is later written.
 */
export function settleOperationsOnLoad() {
    if (!(gameState.syndicate?.operations || []).length) return;
    const result = settleOperationsCore({ now: Date.now(), maxPerSlot: 1 });
    if (result.settled.length) announceSettlement(result, true);
}

function announceSettlement(result, whileAway = false) {
    const names = result.settled.map(o => OPERATION_TEMPLATES[o.templateId]?.name || o.templateId);
    addNotification(
        `🗺️ ${names.join(', ')} arrived${whileAway ? ' while you were away' : ''} · +${result.credits.toLocaleString()}💰${result.writs ? ` +${result.writs}📜` : ''}`,
        'success',
        // Sticky when it landed during an absence: the player was not watching,
        // and a payout they never saw is exactly what stickiness is for.
        whileAway ? { sticky: true } : {}
    );
}

// ---------------------------------------------------------------------------
// Window exposure (onclick-string pattern used across the codebase)
// ---------------------------------------------------------------------------

if (typeof window !== 'undefined') {
    window.cycleOperationEndpoint = cycleOperationEndpoint;
    window.clearOperationRoute = clearOperationRoute;
    window.selectOperation = selectOperation;
    window.selectOperationTemplate = selectOperationTemplate;
    window.setOperationDestination = setOperationDestination;
    window.setOperationOrigin = setOperationOrigin;
    window.setOperationSignature = setOperationSignature;
    window.setOperationLead = setOperationLead;
    window.launchOperation = launchOperation;
}
